import { ReloadOutlined } from '@ant-design/icons'
import { Button, Drawer, Space, Table, Tag, Typography, message } from 'antd'
import { adminApi, formatApiError } from '../api'
import { formatDateOrDash } from './ui'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import type { RelationRow } from './RelationsReviewDrawer'

type ScopeParams = { scope: KnowledgeScope }

export type ConfirmedReviewItem = {
  id: number
  title: string
  entity_type?: string
  category?: string
  relations_json?: RelationRow[] | null
  relations_status?: string
  updated_at?: string
}

type Props = {
  open: boolean
  item: ConfirmedReviewItem | null
  scopeParams: ScopeParams
  onClose: () => void
  onChanged: () => void
}

export function RelationsConfirmedDrawer({
  open,
  item,
  scopeParams,
  onClose,
  onChanged,
}: Props) {
  const rows = (item?.relations_json || []).map((row, i) => ({
    key: String(i),
    source_entity: row.source_entity || '—',
    relation: row.relation || '关联',
    target: row.target || '—',
  }))

  const reextract = () => {
    if (!item) return
    adminApi
      .post<{ queued: boolean; message?: string }>(
        `/admin/knowledge/items/${item.id}/relations/extract`,
        null,
        { params: scopeParams },
      )
      .then((r) => {
        if (!r.data.queued) {
          message.info(r.data.message || '该条目已在抽取队列中')
          return
        }
        message.success('已提交重新抽取，完成后可在「待审核」中查看')
        onChanged()
        onClose()
      })
      .catch((e) => message.error(formatApiError(e, '提交抽取失败')))
  }

  return (
    <Drawer
      title={item?.title || '已审核关系'}
      open={open && item != null}
      onClose={onClose}
      width={720}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>关闭</Button>
          <Button icon={<ReloadOutlined />} onClick={() => void reextract()}>
            重新 AI 抽取
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Tag color="success">已审核</Tag>
        {item?.category ? <Tag>{item.category}</Tag> : null}
        {item?.updated_at ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            更新于 {formatDateOrDash(item.updated_at)}
          </Typography.Text>
        ) : null}
      </Space>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        以下关系已写入知识图谱并可用于 RAG；同步 ES 后可在检索中命中图谱切片。
      </Typography.Text>
      <Table
        size="small"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: '暂无关系（可在知识库编辑条目中手动补充）' }}
        columns={[
          { title: '主体', dataIndex: 'source_entity', width: '34%' },
          { title: '关系', dataIndex: 'relation', width: '22%' },
          { title: '客体', dataIndex: 'target', width: '34%' },
        ]}
      />
    </Drawer>
  )
}
